import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'

import useTableSort from '@/hooks/table/use-table-sort.hook'
import { playerTransactionsSelector } from '@/state/player-transactions/slices/index.slices'
import { uiGlobalSelector } from '@/state/ui/global/slices/index.slices'

import { CurrencyCodeEnum } from '@/enums'
import {
  getDateForTable,
  getDateTimeFromIsoUTC,
  getFormatNumber,
} from '@/utils'
import { TimeZoneUTCType } from '@/enums'
import { dateTimeTableFormat } from '@/constants/date'

import { TableHeadCellPropsType } from '@/state/ui/global/models'
import {
  PlayerTransactionItem,
  XlsxPlayerTransactionType,
} from '@/state/player-transactions/models'

export interface PlayerTransactionTableItem
  extends Omit<PlayerTransactionItem, 'transactionDate'> {
  transactionDate: React.ReactNode | string
  amountText: string
  balanceBeforeText: string
  balanceAfterText: string
  transactionType: string
  transactionStatus: string
  walletType: string
  externalTransactionId: string
  group: string
  subGroup: string
  transactionSubType: string
  performedBy: string
}

export const useTransactions = () => {
  const { t } = useTranslation()

  const { transactions, pagination, isLoading } = useSelector(
    playerTransactionsSelector,
  )

  const { isLoadingFull } = useSelector(uiGlobalSelector)

  const headCells: TableHeadCellPropsType<PlayerTransactionItem>[] = [
    {
      id: 'transactionDate',
      label: t('transaction.transactionDate'),
      sort: true,
    },
    {
      id: 'externalTransactionId',
      label: t('transaction.externalTransactionId'),
      sort: true,
    },
    {
      id: 'transactionType',
      label: t('transaction.transactionTypeLabel'),
      sort: true,
    },
    {
      id: 'transactionStatus',
      label: t('transaction.transactionStatus'),
      sort: true,
    },
    { id: 'walletType', label: t('transaction.walletTypeText'), sort: true },
    { id: 'performedBy', label: t('providers'), sort: true },
    { id: 'group', label: t('transaction.group'), sort: true },
    { id: 'amount', label: t('transaction.transactionAmount'), sort: true },
    { id: 'balanceBefore', label: t('transaction.balanceBefore'), sort: true },
    { id: 'balanceAfter', label: t('transaction.balanceAfter'), sort: true },
    { id: 'actions' as keyof PlayerTransactionItem, label: '', sort: false },
  ]

  const formattedTransactions =
    transactions?.map((transaction) => {
      const formattedDate = transaction.transactionDate
        ? `${format(
            getDateTimeFromIsoUTC(
              transaction.transactionDate,
              TimeZoneUTCType['GMT-07 Pacific Time'],
            ),
            dateTimeTableFormat,
          )} PT`
        : '-'

      return {
        ...transaction,
        transactionDate: transaction.transactionDate
          ? getDateForTable(formattedDate)
          : '-',
        amountText: getFormatNumber(transaction.amount, CurrencyCodeEnum.USD),
        balanceBeforeText: getFormatNumber(
          transaction.balanceBefore,
          CurrencyCodeEnum.USD,
        ),
        balanceAfterText: getFormatNumber(
          transaction.balanceAfter,
          CurrencyCodeEnum.USD,
        ),
      }
    }) || []

  const getXlsFormattedRow = (
    row: PlayerTransactionItem,
  ): XlsxPlayerTransactionType => {
    return {
      transactionDate: row.transactionDate
        ? `${format(
            getDateTimeFromIsoUTC(
              row.transactionDate,
              TimeZoneUTCType['GMT-07 Pacific Time'],
            ),
            dateTimeTableFormat,
          )} PT`
        : '-',
      externalTransactionId: row.externalTransactionId,
      transactionType: row.transactionType,
      walletType: row.walletType,
      performedBy: row.performedBy,
      group: row.group,
      amount: getFormatNumber(row.amount, CurrencyCodeEnum.USD),
      balanceBefore: getFormatNumber(row.balanceBefore, CurrencyCodeEnum.USD),
      balanceAfter: getFormatNumber(row.balanceAfter, CurrencyCodeEnum.USD),
    }
  }

  const sortedInitialState = 'transactionDate'

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    PlayerTransactionTableItem,
    keyof PlayerTransactionTableItem
  >(formattedTransactions, sortedInitialState, 'desc')

  return {
    sortedRows,
    createSortHandler,
    getXlsFormattedRow,
    order,
    orderBy,
    pagination,
    isLoading,
    isLoadingFull,
    headCells,
  }
}
